/*
 * Rice Time — motion enhancements
 * ---------------------------------------------------------------
 * Progressive enhancement only. Every feature here upgrades markup that
 * is already complete and readable; removing this file degrades the page
 * to a static version with no loss of content or navigation.
 *
 * Scroll reveals are NOT handled here — Dawn's animations.js already
 * drives .scroll-trigger, and rt-motion.css hooks those same classes.
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------
   * Marquee
   *
   * The CSS loops by translating the track -50%, which only reads as
   * seamless if the track holds at least two identical runs of content
   * wider than the viewport. Clone until that's true.
   * --------------------------------------------------------------- */
  function buildMarquee(marquee) {
    var track = marquee.querySelector('.rt-marquee__track');
    if (!track || track.dataset.rtBuilt === 'true') return;

    var run = track.querySelector('.rt-marquee__run');
    if (!run) return;

    // Clear any clones from a previous build (viewport resize, editor reload)
    var existing = track.querySelectorAll('.rt-marquee__run');
    for (var i = 1; i < existing.length; i++) existing[i].remove();

    var target = marquee.offsetWidth || window.innerWidth;
    var guard = 0;

    // Fill one viewport width...
    while (track.scrollWidth < target && guard < 30) {
      track.appendChild(run.cloneNode(true));
      guard++;
    }

    // ...then duplicate the whole set so -50% lands on an identical frame.
    var runs = Array.prototype.slice.call(track.querySelectorAll('.rt-marquee__run'));
    runs.forEach(function (node) {
      var clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    track.dataset.rtBuilt = 'true';
  }

  function initMarquees(root) {
    var marquees = (root || document).querySelectorAll('.rt-marquee');
    Array.prototype.forEach.call(marquees, buildMarquee);
  }

  function resetMarquees() {
    var tracks = document.querySelectorAll('.rt-marquee__track');
    Array.prototype.forEach.call(tracks, function (track) {
      track.dataset.rtBuilt = 'false';
    });
    initMarquees();
  }

  /* ---------------------------------------------------------------
   * Headline word rise
   *
   * Splits on words only — never on characters — so screen readers and
   * text selection still get whole words. The original text is preserved
   * on a visually-hidden node for assistive tech.
   * --------------------------------------------------------------- */
  function splitHeadline(el) {
    if (el.dataset.rtReady === 'true') return;

    var text = el.textContent.trim();
    if (!text) return;

    var words = text.split(/\s+/);

    var sr = document.createElement('span');
    sr.className = 'rt-visually-hidden';
    sr.textContent = text;

    var visual = document.createElement('span');
    visual.setAttribute('aria-hidden', 'true');

    words.forEach(function (word, index) {
      var wrap = document.createElement('span');
      wrap.className = 'rt-rise__word';
      wrap.style.setProperty('--rt-word-index', index);

      var inner = document.createElement('span');
      inner.textContent = word;
      wrap.appendChild(inner);
      visual.appendChild(wrap);

      if (index < words.length - 1) {
        visual.appendChild(document.createTextNode(' '));
      }
    });

    el.textContent = '';
    el.appendChild(sr);
    el.appendChild(visual);
    el.dataset.rtReady = 'true';
  }

  function initHeadlines(root) {
    var headlines = (root || document).querySelectorAll('.rt-rise');
    Array.prototype.forEach.call(headlines, function (el) {
      splitHeadline(el);
      // Next frame, so the initial (hidden) state paints before transitioning.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.add('rt-rise--in');
        });
      });
    });
  }

  /* ---------------------------------------------------------------
   * Gold rules — draw in when scrolled into view
   * --------------------------------------------------------------- */
  function initRules(root) {
    var rules = (root || document).querySelectorAll('.rt-rule');
    if (!rules.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(rules, function (el) {
        el.classList.add('rt-rule--in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('rt-rule--in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -60px 0px' }
    );

    Array.prototype.forEach.call(rules, function (el) {
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------
   * Division door tilt
   *
   * Pointer-only. Never bound on touch, where it would fight scrolling.
   * --------------------------------------------------------------- */
  function initDoors(root) {
    if (reduceMotion.matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var doors = (root || document).querySelectorAll('.rt-door');

    Array.prototype.forEach.call(doors, function (door) {
      var inner = door.querySelector('.rt-door__inner');
      if (!inner || door.dataset.rtTilt === 'true') return;
      door.dataset.rtTilt = 'true';

      var frame = null;

      door.addEventListener('pointermove', function (event) {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          var box = door.getBoundingClientRect();
          var px = (event.clientX - box.left) / box.width - 0.5;
          var py = (event.clientY - box.top) / box.height - 0.5;
          inner.style.setProperty('--rt-tilt-y', (px * 5).toFixed(2) + 'deg');
          inner.style.setProperty('--rt-tilt-x', (-py * 5).toFixed(2) + 'deg');
        });
      });

      door.addEventListener('pointerleave', function () {
        if (frame) {
          cancelAnimationFrame(frame);
          frame = null;
        }
        inner.style.setProperty('--rt-tilt-y', '0deg');
        inner.style.setProperty('--rt-tilt-x', '0deg');
      });
    });
  }

  /* ---------------------------------------------------------------
   * Grain interaction (click-to-sizzle and the minigame) lives in
   * assets/rt-grain-game.js, which is loaded only by the hero section.
   * It is deliberately NOT here: this file ships on every page, and a
   * game engine has no business in shared foundation.
   * --------------------------------------------------------------- */

  function init(root) {
    initMarquees(root);
    initHeadlines(root);
    initRules(root);
    initDoors(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  var resizeTimer;
  window.addEventListener(
    'resize',
    function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resetMarquees, 250);
    },
    { passive: true }
  );

  // Theme editor: re-run when a section is re-rendered.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})();
